import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, ROLE_LABEL } from '@/lib/auth';
import { ProaktifMark } from '@/components/PemkotLogo';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Crown,
  Calendar,
  CalendarDays,
  Plane,
  CheckSquare,
  FileBarChart,
  Shield,
  Users,
  Building2,
  Bell,
  Settings,
  UserCircle2,
  MapPinned,
  Palette,
  MessageCircle,
} from 'lucide-react';

function NavGroup({ label, children }) {
  return (
    <div className="px-3 py-2">
      <div className="px-3 mb-1.5 text-[10px] uppercase tracking-widest font-semibold text-slate-400">{label}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, exact = false, end = false }) {
  const { pathname } = useLocation();
  const active = end ? pathname === to : pathname === to || pathname.startsWith(to + '/');
  return (
    <Link
      to={to}
      data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={cn(
        'relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
        active
          ? 'bg-red-50 text-red-700 nav-active'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
      )}
    >
      <Icon className={cn('w-4 h-4 flex-shrink-0', active ? 'text-red-600' : 'text-slate-400')} />
      <span className="truncate">{label}</span>
      {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-400" />}
    </Link>
  );
}

export default function Sidebar({ onNavigate }) {
  const { user } = useAuth();
  if (!user) return null;
  const role = user.role;

  return (
    <div className="w-full h-full flex flex-col bg-white">
      <div className="h-16 border-b border-slate-200 px-4 flex items-center">
        <ProaktifMark />
      </div>

      <div className="flex-1 overflow-y-auto py-2" onClick={onNavigate}>
        <NavGroup label="Utama">
          {role === 'pimpinan' && <NavItem to="/command-center" icon={Crown} label="Command Center" />}
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />
          <NavItem to="/keberadaan" icon={MapPinned} label="Keberadaan Pejabat" />
        </NavGroup>

        <NavGroup label="Agenda Pimpinan">
          <NavItem to="/agenda" icon={Calendar} label="Daftar Agenda" end />
          <NavItem to="/agenda/kalender" icon={CalendarDays} label="Kalender" />
          {(role === 'admin' || role === 'kabag') && (
            <NavItem to="/approval" icon={CheckSquare} label="Approval Inbox" />
          )}
        </NavGroup>

        <NavGroup label="Perjalanan Dinas">
          <NavItem to="/sppd" icon={Plane} label="Monitoring SPPD" />
        </NavGroup>

        <NavGroup label="Laporan & Audit">
          <NavItem to="/laporan" icon={FileBarChart} label="Laporan" />
          {(role === 'admin' || role === 'kabag' || role === 'pimpinan') && (
            <NavItem to="/audit" icon={Shield} label="Audit Trail" />
          )}
        </NavGroup>

        {(role === 'admin' || role === 'operator') && (
          <NavGroup label="Master Data">
            <NavItem to="/manajemen/pejabat" icon={UserCircle2} label="Master Pejabat" />
            {role === 'admin' && <NavItem to="/manajemen/user" icon={Users} label="User Management" />}
            {role === 'admin' && <NavItem to="/manajemen/opd" icon={Building2} label="Referensi OPD" />}
          </NavGroup>
        )}

        {role === 'admin' && (
          <NavGroup label="Pengaturan">
            <NavItem to="/pengaturan/branding" icon={Palette} label="Branding & Logo" />
            <NavItem to="/pengaturan/whatsapp" icon={MessageCircle} label="WhatsApp Fonnte" />
          </NavGroup>
        )}

        <NavGroup label="Akun">
          <NavItem to="/notifikasi" icon={Bell} label="Notifikasi" />
          <NavItem to="/profil" icon={Settings} label="Profil" />
        </NavGroup>
      </div>

      <div className="border-t border-slate-200 p-3">
        <div className="flex items-center gap-2.5 p-2 rounded-lg bg-slate-50">
          <div className="w-9 h-9 rounded-full bg-red-600 text-white flex items-center justify-center font-semibold">
            {user.full_name?.[0] || 'U'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-800 truncate">{user.full_name}</div>
            <div className="text-[11px] text-slate-500 truncate">{ROLE_LABEL[user.role] || user.role}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
